import { PharmacistLayout } from "@/components/layout/pharmacist-layout";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, Trash2, Loader2, CheckCircle2, AlertTriangle, User, ShieldCheck } from "lucide-react";
import { RegistrationVerificationPanel } from "@/components/ui/registration-verification-panel";
import { BrowserStorageCard } from "@/components/ui/browser-storage-card";
import { TeamCodeCard } from "@/components/settings/team-code-card";


const BASE_URL = import.meta.env.BASE_URL.replace(/\/$/, "");

interface DataSummary {
  conversations: number;
  messages: number;
  aiAuditLogs: number;
  lastAiInteraction: string | null;
}

interface DeleteResult {
  success: boolean;
  deleted: { conversations: number; messages: number; aiAuditLogs: number };
  deletedAt: string;
}

export default function PharmacistSettings() {
  const [summary, setSummary] = useState<DataSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteResult | null>(null);

  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetch("/api/user/data/summary")
      .then((r) => r.json())
      .then((data) => { setSummary(data); setSummaryLoading(false); })
      .catch(() => { setSummaryError("Could not load your data summary."); setSummaryLoading(false); });
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch("/api/user/data/export");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rheumhub-pharmacist-data-export-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch("/api/user/data", { method: "DELETE" });
      const result = await response.json();
      setDeleteResult(result);
      setSummary({ conversations: 0, messages: 0, aiAuditLogs: 0, lastAiInteraction: null });
      setDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <PharmacistLayout title="Your Data & Settings">
      <div className="max-w-2xl mx-auto space-y-8">

        <div className="flex items-start gap-4">
          <div className="bg-violet-50 p-3 rounded-2xl">
            <User className="h-7 w-7 text-violet-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">Pharmacist Settings</h2>
            <p className="text-slate-500 text-sm mt-0.5">Manage your data under UK GDPR. You have the right to access and erase your data.</p>
          </div>
        </div>

        {/* Professional verification */}
        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-violet-600" />
            <h3 className="font-bold text-slate-900">Professional verification</h3>
          </div>
          <RegistrationVerificationPanel userRole="pharmacist" />
        </section>

        <TeamCodeCard accent="violet" />

        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Data summary</h3>
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading...</div>
          ) : summaryError ? (
            <p className="text-red-600 text-sm">{summaryError}</p>
          ) : summary ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-slate-900">{summary.conversations}</p>
                <p className="text-sm text-slate-500">Conversations</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-slate-900">{summary.messages}</p>
                <p className="text-sm text-slate-500">Messages</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-2xl font-bold text-slate-900">{summary.aiAuditLogs}</p>
                <p className="text-sm text-slate-500">AI interactions logged</p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm font-semibold text-slate-900">
                  {summary.lastAiInteraction
                    ? new Date(summary.lastAiInteraction).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "No AI interactions yet"}
                </p>
                <p className="text-sm text-slate-500">Last AI interaction</p>
              </div>
            </div>
          ) : null}
          <p className="text-xs text-slate-400">Pharmacist data is retained for up to 365 days after last activity, then automatically deleted.</p>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900">Export your data</h3>
          <p className="text-sm text-slate-600">
            Download a copy of all your conversations, messages, and AI audit log metadata in JSON format. This fulfils your right to data portability under UK GDPR.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-violet-600 hover:bg-violet-700 text-white"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Export my data (JSON)
          </Button>
        </section>

        <section className="bg-white rounded-2xl border border-red-200 p-8 space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            Delete all my data
          </h3>
          <p className="text-sm text-slate-600">
            This will permanently delete all your conversations, messages, and AI audit log entries. This action cannot be undone. Your account will remain active.
          </p>

          {deleteResult ? (
            <div className="flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl p-4 text-sm text-teal-800">
              <CheckCircle2 className="h-5 w-5 text-teal-500 shrink-0" />
              <div>
                <p className="font-semibold">Data deleted successfully</p>
                <p>Removed {deleteResult.deleted.conversations} conversations, {deleteResult.deleted.messages} messages, and {deleteResult.deleted.aiAuditLogs} audit log entries.</p>
              </div>
            </div>
          ) : deleteConfirm ? (
            <div className="space-y-3">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
                <p className="font-semibold">Are you sure?</p>
                <p>This will permanently delete all your RheumHub data. This cannot be undone.</p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Yes, delete everything
                </Button>
                <Button variant="outline" onClick={() => setDeleteConfirm(false)} disabled={deleting}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm(true)}
              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete all my data
            </Button>
          )}
        </section>

        <BrowserStorageCard />

        <p className="text-center text-xs text-slate-400">
          For questions about your data, contact <strong>privacy@medantixhealth.co.uk</strong>.{" "}
          <a href="/privacy" className="underline hover:text-slate-600">Privacy Policy</a>
        </p>

      </div>
    </PharmacistLayout>
  );
}
